import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { ReactComponent as WarningIcon } from 'src/icons/untitled-ui/disclaimerWarning.svg';

/**
 * Alert modal shown when a user opens a new survey link while another survey is already open in another tab.
 * Informative only: instructs user to complete or close the other survey first.
 */
export const SurveyConflictAlert = ({ open = false, onClose, activeSurveyName }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="column" 
        spacing={1.5} 
        alignItems="center" 
        justifyContent="center">
          <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningIcon width="48" 
            height="48" />
          </Box>
          <Typography variant="h6" 
          fontWeight="bold">
            Survey Already Active in Another Tab
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <Typography variant="body1" 
        color="text.secondary" 
        sx={{ mb: 1.5 }}>
          You currently have another survey open in a different browser tab
          {activeSurveyName ? ` (${activeSurveyName})` : ''}.
        </Typography>
        <Typography variant="body2" 
        color="text.secondary">
          To protect your progress and avoid conflicting responses, please complete or close your existing survey tab before opening this one.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pt: 1, pb: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          sx={{ minWidth: 140, px: 3, py: 1, fontWeight: 'bold' }}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SurveyConflictAlert.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activeSurveyName: PropTypes.string,
};

/**
 * Blocked screen displayed in place of the survey content if the user dismissed the modal
 * but the conflicting survey tab is still open.
 */
export const SurveyBlockedScreen = ({ activeSurveyName }) => {
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 520, width: '100%', textAlign: 'center', p: 4, borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <WarningIcon width="56" 
            height="56" />
          </Box>
          <Typography variant="h5" 
          fontWeight="bold" 
          sx={{ mb: 1.5 }}>
            Survey Session Paused
          </Typography>
          <Typography variant="body1" 
          color="text.secondary" 
          sx={{ mb: 2 }}>
            Another survey tab is actively open{activeSurveyName ? ` (${activeSurveyName})` : ''}.
            Please return to your previous tab to complete or close that survey.
          </Typography>
          <Typography variant="body2" 
          color="text.secondary" 
          sx={{ mb: 3 }}>
            Once you close the other survey tab, refresh this page to begin this survey.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ px: 4, py: 1, fontWeight: 'bold' }}
          >
            Refresh Page
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

SurveyBlockedScreen.propTypes = {
  activeSurveyName: PropTypes.string,
};
